import { get, writable } from 'svelte/store';

import { checkConnections } from '@client/utils/sync-connections';

import { activeDomain } from './domain-settings';

/**
 * Type definition for connection settings status.
 * @typedef {Object} ConnectionSettings
 * @property {boolean} [checkairtableconnected] - Status of Airtable connection.
 * @property {boolean} [checkbamboohrconnected] - Status of BambooHR connection.
 * @property {boolean} [checkcustomdirconnected] - Status of Custom Directory connection.
 * @property {boolean} [checkfrontappconnected] - Status of FrontApp connection.
 * @property {boolean} [checkftpconnected] - Status of FTP connection.
 * @property {boolean} [checkgappsconnected] - Status of Google Apps connection.
 * @property {boolean} [checko365connected] - Status of Office 365 connection.
 * @property {boolean} [checkoktaconnected] - Status of Okta connection.
 * @property {boolean} [checkoneloginconnected] - Status of OneLogin connection.
 * @property {boolean} [checkoutreachconnected] - Status of Outreach connection.
 * @property {boolean} [checkpaylocityconnected] - Status of Paylocity connection.
 * @property {boolean} [checkpingboardconnected] - Status of Pingboard connection.
 * @property {boolean} [checksalesforceconnected] - Status of Salesforce connection.
 * @property {boolean} [checktotalexpertconnected] - Status of Total Expert connection.
 */

/**
 * @typedef {Record<string, ConnectionSettings>} DomainCacheStoreValue
 * An object where keys are domain strings and values are ConnectionSettings objects.
 */

/** @type {import('svelte/store').Writable<ConnectionSettings>} */
const currentConnections = writable({});

/** @type {import('svelte/store').Writable<DomainCacheStoreValue>} */
const domainCache = writable({});

/** @type {import('svelte/store').Writable<boolean>} */
const loadingConnections = writable(false);

// Keep track of the last domain fetched/checked to potentially avoid redundant calls
let lastCheckedDomain = null;

// Store the active domain listener's unsubscribe function
let unsubscribeFromActiveDomain = null;

// Track active subscribers to syncConnections
let subscriberCount = 0;

// Prevent concurrent fetches for the same domain
let fetchInProgressForDomain = null;

/**
 * Fetches connection settings for a given domain, utilizing a cache first.
 * If not cached, fetches from the API and updates the cache and potentially the UI store.
 * Prevents concurrent fetches for the same domain.
 *
 * @param {string} domain - The domain for which to fetch connection settings.
 * @returns {Promise<void>} A promise that resolves when the fetch/cache check is complete.
 */
async function fetchOrGetCachedConnections(domain) {
  // Prevent starting a new fetch if one for the *same domain* is already running
  if (fetchInProgressForDomain === domain) {
    console.log(`Check already in progress for domain: ${domain}. Waiting...`);
    return;
  }

  // Check cache
  const cachedSettings = get(domainCache)[domain];

  if (cachedSettings) {
    console.log(`Using cached connection settings for domain: ${domain}`);
    currentConnections.set(cachedSettings);
    loadingConnections.set(false);
    lastCheckedDomain = domain;
    fetchInProgressForDomain = null; // Ensure flag is cleared if we hit cache

    return;
  }

  // Not in cache so fetch from API
  console.log(`Cache miss. Fetching connection settings for domain: ${domain}`);
  loadingConnections.set(true);
  lastCheckedDomain = domain; // Mark this domain as the one being checked/fetched
  fetchInProgressForDomain = domain; // Set flag for this domain

  try {
    // Assuming checkConnections returns an object like { connectionSettings: ConnectionSettings }
    const { connectionSettings } = await checkConnections();

    const currentActiveDomain = get(activeDomain);

    // Update the cache for the domain the fetch was for.
    domainCache.update((cache) => {
      cache[domain] = connectionSettings;
      return cache;
    });
    console.log(`Successfully cached connections for domain: ${domain}`);

    // Update the UI store (currentConnections) only if the domain is still active.
    if (currentActiveDomain === domain) {
      currentConnections.set(connectionSettings);
      console.log(`UI store updated for currently active domain: ${domain}`);
    } else {
      // Don't update the UI store if the active domain has changed.
      console.log(`UI store NOT updated for ${domain}, active domain is now ${currentActiveDomain}.`);
    }
  } catch (error) {
    console.error(`Failed to fetch connection settings for domain ${domain}`, error);
    // Reset current connections if the failed fetch was for the active domain
    if (get(activeDomain) === domain) {
      currentConnections.set({});
    }
  } finally {
    // Clear the fetch-in-progress flag *only if* it matches the domain this function instance was handling
    if (fetchInProgressForDomain === domain) {
      fetchInProgressForDomain = null;
    }
    // Set loading to false only if no other fetch is in progress AND the active domain matches the one just handled
    // This prevents prematurely setting loading to false if another fetch started for a different domain while this one was running.
    if (fetchInProgressForDomain === null && get(activeDomain) === domain) {
      loadingConnections.set(false);
    }
  }
}

/**
 * Creates a Svelte store-compatible object that provides synchronized connection settings.
 * It automatically fetches/updates settings when the active domain changes or when the first subscriber joins.
 * It manages caching and prevents redundant API calls.
 * @returns {{subscribe: function(function(ConnectionSettings): void, function(ConnectionSettings=): void): function(): void}} A Svelte store-like object with a subscribe method.
 */
function createSyncConnectionsStore() {
  const store = {
    /**
     * Subscribe to changes in connection settings.
     * Manages fetching data and listening to domain changes based on subscriber count.
     *
     * @param {function(ConnectionSettings): void} run - The callback function that receives the store's value. Runs initially and whenever the value changes.
     * @param {function(ConnectionSettings=): void} [invalidate] - Optional callback used by Svelte for reactive invalidation.
     * @returns {function(): void} A function to unsubscribe the listener.
     */
    subscribe(run, invalidate) {
      const unsubscribeFromCurrent = currentConnections.subscribe(run, invalidate);

      subscriberCount++;

      // If this is the first subscriber, trigger the initial check AND set up domain watching
      if (subscriberCount === 1) {
        const currentDomain = get(activeDomain);
        // Only fetch if a domain is actually set
        if (currentDomain) {
          fetchOrGetCachedConnections(currentDomain);
        } else {
          // Ensure initial state is empty and not loading if no domain
          currentConnections.set({});
          loadingConnections.set(false);
          lastCheckedDomain = null;
        }

        // Start listening to activeDomain changes while subscribed
        unsubscribeFromActiveDomain = activeDomain.subscribe(($activeDomain) => {
          // Handle null/undefined domain - reset state
          if (!$activeDomain) {
            console.log('Active domain cleared. Resetting connections.');
            currentConnections.set({});
            loadingConnections.set(false);
            lastCheckedDomain = null;
            fetchInProgressForDomain = null; // Clear any potential in-progress fetch flag

            return; // Stop further processing for null domain
          }

          // Avoid checking if the domain hasn't actually changed relative to the last check triggered by this listener.
          // Also trigger if the domain is the same but not cached (e.g., previous fetch failed) and not currently fetching it.
          const isCached = !!get(domainCache)[$activeDomain];
          const needsCheck =
            $activeDomain !== lastCheckedDomain || (!isCached && fetchInProgressForDomain !== $activeDomain);

          if (needsCheck) {
            fetchOrGetCachedConnections($activeDomain);
          }
        });
      } else {
        // For subsequent subscribers, ensure data for the *current* domain is loaded if needed.
        // This handles cases where:
        // 1. A domain change happened *before* this subscriber joined, but after the last one left.
        // 2. The initial fetch for the first subscriber failed.
        const currentDomain = get(activeDomain);
        if (currentDomain) {
          // Only proceed if there is a current domain
          const isCached = !!get(domainCache)[currentDomain];
          const isLoading = get(loadingConnections);

          // Trigger fetch only if we have a domain, it's not cached, and not already loading for *any* domain
          // (fetchInProgressForDomain check prevents re-triggering if already loading *this* domain)
          if (!isCached && !isLoading && fetchInProgressForDomain !== currentDomain) {
            fetchOrGetCachedConnections(currentDomain);
          }
        } else {
          // If subscribing when there's no domain, ensure state is clean
          run({}); // Run the subscriber with empty data
        }
      }

      // Return the unsubscribe function
      return () => {
        unsubscribeFromCurrent(); // Unsubscribe the caller from the data store
        subscriberCount--;

        // If this was the last subscriber, clean up the domain listener
        if (subscriberCount === 0) {
          if (unsubscribeFromActiveDomain) {
            unsubscribeFromActiveDomain();
            unsubscribeFromActiveDomain = null;
          }
          lastCheckedDomain = null; // Reset state when no one is listening
          fetchInProgressForDomain = null; // Clear any potential in-progress fetch flag
        }
      };
    }
  };

  return store;
}

export { loadingConnections };
export const syncConnections = createSyncConnectionsStore();
